/**
 * Maps backend validation error codes to user-friendly Portuguese messages.
 *
 * These codes are returned by the backend when bill validation fails
 * during status transitions (e.g., PENDING_APPROVAL, APPROVED).
 */

import {
  COST_SERVICE_ERROR_MESSAGES,
  fillCostServiceCopy,
  REVENUE_CYCLE_ERROR_MESSAGES,
  REVENUE_TAX_ERROR_MESSAGES,
  WORKFLOW_DESCRIPTION_ERROR_MESSAGES,
  WORKFLOW_ORIGIN_ERROR_MESSAGES,
  WORKFLOW_RUN_LOG_WRITE_FAILED_COPY,
  WORKFLOW_TRIGGER_NOT_RUNNABLE_COPY,
  type WorkflowDescriptionErrorCode,
  type WorkflowOriginErrorCode,
} from '@alinea/types';
import {
  WorkflowConditionErrorCode,
  WorkflowConditionFactErrorCode,
  WorkflowTriggerErrorCode,
} from '@alinea/types/wf';

import i18n from '@/lib/i18n';

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
  /** The values the coded copy names (D45c: `{conflictLabel}`, `{bookName}`, …). */
  details?: Record<string, unknown>;
}

/**
 * Portuguese labels for bill fields that the backend can mark as
 * `REQUIRED_FIELD_MISSING`. Keys mirror the actual bill property names
 * returned by `mapRequiredBillFieldToActualField` on the API side.
 */
const REQUIRED_FIELD_MESSAGES: Record<string, string> = {
  vendorId: 'Selecione o fornecedor',
  description: 'Informe a descrição',
  dueDate: 'Informe a data de vencimento',
  billCompetencyDate: 'Informe a data de competência',
  billManagerialDate: 'Informe a data gerencial',
  billIssueDate: 'Informe a data de emissão',
  paymentMethod: 'Informe a forma de pagamento',
  totalAmount: 'Informe o valor total',
  source: 'Informe a origem',
};

/**
 * Error code to Portuguese message mapping for bill approval validation errors.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  WORKFLOW_ENTITY_MISMATCH:
    'A regra referencia classificações de entidades diferentes. Use classificações de uma única entidade.',
  WORKFLOW_ENTITY_REQUIRED:
    'Não foi possível determinar a entidade desta regra. Selecione classificações válidas de uma única entidade.',
  WORKFLOW_ENTITY_NOT_FOUND:
    'A entidade selecionada não existe ou não está disponível neste tenant.',
  WORKFLOW_ENTITY_NOT_APPLICABLE: 'Este tipo de fluxo não aceita uma entidade vinculada à regra.',
  WORKFLOW_ENTITY_UNRESOLVED_REFERENCE:
    'Uma classificação referenciada pela regra não existe ou não possui vínculo de entidade válido.',

  // Fact conditions (ALC-4623): fields that exist on classification rules (bill updated) only
  [WorkflowConditionFactErrorCode.FIELD_NOT_ALLOWED_FOR_TRIGGER]:
    'Este campo só pode ser usado em regras de classificação de contas.',
  [WorkflowConditionFactErrorCode.OPERATOR_NOT_ALLOWED]:
    'O operador escolhido não pode ser usado com este campo.',
  [WorkflowConditionFactErrorCode.VALUE_INVALID]: 'O valor informado não é válido para este campo.',
  [WorkflowConditionFactErrorCode.PROFILE_RATE_REQUIRED]:
    'Informe a alíquota de cada imposto do perfil de retenção.',
  [WorkflowConditionFactErrorCode.SPLIT_SUM_NOT_100]:
    'Os percentuais de cada dimensão do rateio devem somar exatamente 100,00%.',

  // Condition fields and operators the evaluator cannot read (ALC-4621 R18)
  [WorkflowConditionErrorCode.OPERATOR_INVALID]: 'O operador desta condição não existe.',
  [WorkflowConditionErrorCode.FIELD_UNSUPPORTED]:
    'Este campo não pode ser usado em condições deste gatilho.',
  [WorkflowConditionErrorCode.OPERATOR_UNSUPPORTED_FOR_FIELD]:
    'Este operador não pode ser usado com este campo.',

  // Line quantifier (ALC-4627): "at least one line" / "every line" on a line-item condition
  [WorkflowConditionErrorCode.LINE_QUANTIFIER_NOT_LINE_FIELD]:
    'Escolher "pelo menos uma linha" ou "todas as linhas" só é possível em condições sobre os itens da conta.',
  [WorkflowConditionErrorCode.LINE_QUANTIFIER_TRIGGER_UNSUPPORTED]:
    'Condições sobre os itens da conta só valem em regras de classificação de contas.',

  // Non-runnable trigger (ALC-4621 owner Q1): a trigger that is seeded but has no runner
  [WorkflowTriggerErrorCode.NOT_RUNNABLE]: WORKFLOW_TRIGGER_NOT_RUNNABLE_COPY['pt-BR'],

  // Header field validation
  VENDOR_REQUIRED: 'Selecione um fornecedor',
  BILL_DATE_REQUIRED: 'Informe a data da conta',
  DUE_DATE_REQUIRED: 'Informe o vencimento',
  TOTAL_AMOUNT_REQUIRED: 'Informe o valor total',
  TOTAL_AMOUNT_INVALID: 'O valor total deve ser maior que zero',
  DESCRIPTION_REQUIRED: 'Informe a descrição',
  REQUIRED_FIELD_MISSING: 'Campo obrigatório não preenchido',

  // Classification validation
  CLASSIFICATION_MISSING: 'Classificação não encontrada',
  GL_ACCOUNT_REQUIRED: 'Conta contábil (GL) é obrigatória',
  MANAGERIAL_ACCOUNT_REQUIRED: 'Conta gerencial é obrigatória',
  COST_CENTER_REQUIRED: 'Centro de custo é obrigatório',
  DEPARTMENT_REQUIRED: 'Departamento é obrigatório',
  REQUIRED_CUSTOM_FIELD_MISSING: 'Campo customizado obrigatório não preenchido',

  // Line item level validation
  LINE_ITEMS_MISSING: 'A conta não possui itens para classificação por item',
  LINE_ITEM_CLASSIFICATION_MISSING: 'Classificação do item não encontrada',
  LINE_ITEM_GL_ACCOUNT_REQUIRED: 'Conta contábil (GL) é obrigatória para este item',
  LINE_ITEM_MANAGERIAL_ACCOUNT_REQUIRED: 'Conta gerencial é obrigatória para este item',
  LINE_ITEM_COST_CENTER_REQUIRED: 'Centro de custo é obrigatório para este item',
  LINE_ITEM_DEPARTMENT_REQUIRED: 'Departamento é obrigatório para este item',
  LINE_ITEM_AMOUNT_MISSING:
    'O valor do item de linha é obrigatório para validação da classificação',

  // Payment validation
  PAYMENTS_MISSING: 'É necessário adicionar pelo menos um pagamento antes de enviar para aprovação',
  PAYMENT_AMOUNT_MISMATCH:
    'O total dos pagamentos não corresponde ao valor líquido da conta. Ajuste os pagamentos antes de enviar.',
  BILL_AMOUNT_MISSING: 'O valor total da conta é obrigatório para validação dos pagamentos',
  BOLETO_INVALID: 'Revise o código do boleto ou guia antes de enviar para aprovação',

  // GL account dimension validation
  GL_ACCOUNT_NOT_FOUND: 'Conta contábil não encontrada',
  GL_ACCOUNT_INACTIVE: 'Conta contábil está inativa',
  GL_ACCOUNT_NOT_POSTABLE: 'Apenas contas contábeis lançáveis podem ser usadas na classificação',

  // Managerial account dimension validation
  MANAGERIAL_ACCOUNT_NOT_FOUND: 'Conta gerencial não encontrada',
  MANAGERIAL_ACCOUNT_INACTIVE: 'Conta gerencial está inativa',
  MANAGERIAL_ACCOUNT_WRONG_ENTITY: 'Conta gerencial não pertence a esta entidade',
  MANAGERIAL_ACCOUNT_NOT_LEAF: 'Apenas contas gerenciais analíticas podem ser usadas',

  // Department/cost center/segment dimension validation
  DEPARTMENT_NOT_FOUND: 'Departamento não encontrado ou excluído',
  COST_CENTER_NOT_FOUND: 'Centro de custo não encontrado ou excluído',
  DEPARTMENT_INACTIVE: 'Este departamento está inativo. Escolha um departamento ativo.',
  COST_CENTER_INACTIVE: 'Este centro de custo está inativo. Escolha um centro de custo ativo.',
  SEGMENT_NOT_FOUND: 'Campo customizado não encontrado ou excluído',
  SEGMENT_VALUE_NOT_FOUND: 'Valor do campo customizado não encontrado ou excluído',
  SEGMENT_NOT_PREDEFINED: 'O campo customizado não aceita valores predefinidos',
  SEGMENT_TEXT_VALUE_NOT_ALLOWED: 'Este campo customizado não aceita um valor de texto livre',
  SEGMENT_TEXT_VALUE_BUDGETED_SEGMENT:
    'Este campo customizado possui orçamento. Selecione um dos valores predefinidos em vez de digitar um valor.',
  SEGMENT_TEXT_VALUE_WORKFLOW_SEGMENT:
    'Este campo customizado é usado por uma condição de fluxo de aprovação. Selecione um dos valores predefinidos em vez de digitar um valor.',
  SEGMENT_TEXT_VALUE_TOO_LONG: 'O valor do campo customizado excede 200 caracteres',
  SEGMENT_VALUE_REQUIRED: 'Informe o valor do campo customizado',
  SEGMENT_VALUE_CONFLICT:
    'O campo customizado não pode ter um valor predefinido e um valor de texto ao mesmo tempo',
  SEGMENT_TEXT_VALUE_SINGLE_SPLIT: 'Este campo customizado não aceita mais de um valor de texto',
  SEGMENT_TEXT_VALUE_PERCENT_INVALID: 'O valor de texto do campo customizado deve ser 100%',
  RECURRING_TEMPLATE_TEXT_SEGMENT_UNSUPPORTED:
    'Remova ou substitua os valores de texto livre dos campos customizados nesta conta antes de torná-la recorrente.',

  // Classification split totals
  INVALID_AMOUNT_TOTAL: 'O total dos valores alocados não corresponde ao valor da conta',
  INVALID_PERCENT_TOTAL: 'O total dos percentuais deve ser igual a 100%',
  INVALID_MECE_AMOUNT: 'Os valores dos rateios MECE devem totalizar o valor da conta',
  INVALID_MECE_PERCENT: 'Os percentuais dos rateios MECE devem totalizar 100%',
  CUSTOM_BREAKDOWN_MISSING: 'Rateio personalizado é obrigatório para este tipo de alocação',
  CUSTOM_BREAKDOWN_GL_REQUIRED:
    'Conta contábil é obrigatória em cada linha do rateio personalizado',
  INVALID_CUSTOM_BREAKDOWN_AMOUNT: 'O total dos valores do rateio personalizado não confere',
  INVALID_CUSTOM_BREAKDOWN_PERCENT: 'O total dos percentuais do rateio personalizado deve ser 100%',
  CUSTOM_BREAKDOWN_WITH_FLAT_SPLITS:
    'Rateio personalizado não pode ser combinado com dimensões avulsas',
  CUSTOM_BREAKDOWN_NOT_ALLOWED:
    'Rateio personalizado só é permitido quando o tipo de alocação for CUSTOM_BREAKDOWN',

  // Classification mode
  CLASSIFICATION_MODE_MISMATCH: 'Modo de classificação incompatível com o modo atual da conta',
  LINE_ITEM_NOT_FOUND: 'Item de linha não encontrado para esta conta',
  TARGET_AMOUNT_MISSING: 'O valor alvo é obrigatório para validação do rateio',
  SPLIT_ID_MISSING: 'Identificador de rateio é obrigatório para rastreabilidade',
  SPLIT_ID_DUPLICATE: 'Identificadores de rateio devem ser únicos',
  BILL_NOT_FOUND: 'Conta não encontrada',
  BILL_ENTITY_CHANGE_BLOCKED_BY_CLASSIFICATION:
    'Esta conta está classificada com dimensões da entidade atual. Limpe a classificação antes de trocar a entidade.',
  BILL_ENTITY_CHANGE_BLOCKED_BY_GL_POSTING:
    'A entidade não pode ser alterada após o lançamento contábil da conta.',
  BULK_STATUS_NOT_ELIGIBLE: 'Esta conta não está em um status permitido para envio à aprovação',

  // Fiscal / withholdings
  FISCAL_SERVICE_TYPE_REQUIRED:
    'O tipo de serviço fiscal é obrigatório quando há retenções de IRRF, PIS/COFINS/CSLL ou INSS',
  STATUTORY_REMAP_VERSION_NOT_PERIOD_PIN:
    'O plano de contas informado não é o plano vinculado ao período deste lançamento. Refaça o mapeamento no plano do período e não informe outra versão.',
  STATUTORY_SETTLEMENT_ACCOUNT_INVALID:
    'A conta de recolhimento precisa ser uma conta de passivo credora e lançável do plano vinculado ao período. Selecione outra conta nesse plano.',
  ACCOUNTING_SCOPE_PERIOD_NOT_FOUND:
    'O período contábil informado não foi encontrado para a entidade e o livro da fatura. Revise a competência ou selecione um período válido antes de remapear.',

  // Reimbursement
  REIMBURSEMENT_REQUEST_MISSING: 'Preencha as informações do reembolso antes de enviar',
  REIMBURSEMENT_REQUESTER_REQUIRED: 'Selecione o solicitante do reembolso',
  REIMBURSEMENT_DUE_DATE_REQUIRED: 'Informe o vencimento do reembolso',
  REIMBURSEMENT_PAYMENT_METHOD_REQUIRED: 'Selecione o método de reembolso',
  REIMBURSEMENT_ORIGIN_ACCOUNT_REQUIRED: 'Selecione a conta de origem do reembolso',

  // GL posting
  NO_OPEN_PERIOD:
    'Essa conta não pode ser aprovada porque não há período contábil aberto para a data de competência selecionada',

  // Budget
  BUDGET_EXCEEDED:
    'Esta conta excede o limite orçamentário configurado e não pode ser enviada para aprovação',
  BUDGET_EXCEPTION_REQUIRED:
    'Esta conta excede o orçamento e requer uma exceção aprovada antes da aprovação final',

  // Procurement / purchase order
  PURCHASE_ORDER_REQUIRED:
    'Selecione um pedido de compra antes de enviar esta conta para aprovação',
  EXPIRED: 'O pedido de compra selecionado está expirado',
  FULFILLED: 'O pedido de compra selecionado já foi totalmente faturado',
  NOT_READY: 'O pedido de compra precisa ser marcado como pronto antes de vincular a uma conta',
  NOT_RECEIVED: 'O pedido de compra ainda não foi recebido',
  AMOUNT_EXCEEDED: 'O valor da conta excede o saldo disponível no pedido de compra',
  VENDOR_NOT_ALLOWED: 'O fornecedor da conta não está vinculado ao pedido de compra selecionado',
  GL_CLASSIFICATION_STALE:
    'A conta contábil classificada pertence a uma versão do plano de contas diferente da vigente para esta fatura. Reclassifique a conta antes de converter em fatura.',
  GL_CLASSIFICATION_INCOMPLETE:
    'A conta contábil classificada não tem versão do plano de contas registrada, então não pode ser validada contra a versão desta fatura. Reclassifique a conta antes de converter em fatura.',
  PROCUREMENT_GL_ACCOUNT_WRONG_COA_VERSION:
    'A conta contábil selecionada pertence a outra versão do plano de contas desta entidade. Selecione uma conta do plano de contas vigente.',
  GL_ACCOUNT_COA_VERSION_MISMATCH:
    'A conta contábil selecionada pertence a outra versão do plano de contas. Selecione uma conta da versão vigente para a competência desta conta.',

  // Currency review (readiness validation uses structured errors[])
  AP_BILL_CURRENCY_REVIEW_REQUIRED:
    'A moeda extraída deste documento precisa ser confirmada antes de continuar',
  // ALC-4559: the document differs from the vendor rule (readiness gate)
  AP_BILL_VENDOR_RULE_DIVERGENCE_OPEN:
    'Os valores do documento diferem da regra do fornecedor. Escolha como resolver a divergência na seção de retenções antes de enviar para aprovação.',
  AP_WITHHOLDING_SLOT_UNSUPPORTED:
    'Esta conta possui retenção tributária sem conta contábil equivalente no plano de contas.',

  // Credit card invoice
  CARD_PROVIDER_REQUIRED: 'Informe a operadora do cartão de crédito para notas de fatura de cartão',

  // Client reimbursable
  CLIENT_REIMBURSABLE_BILL_HAS_TAX_CREDITS:
    'Contas reembolsáveis pelo cliente não podem ter créditos de impostos (PIS/COFINS ou IVA). Limpe os créditos antes de enviar',
};

const LOCALIZED_ERROR_CODE_MESSAGES: Record<string, Partial<Record<'en' | 'pt-BR', string>>> = {
  // The GL could not find the account a tax slot posts to (the tax-account resolver in
  // packages/services). The API's sentence names the slot code, which means nothing to the user.
  TAX_ACCOUNT_NOT_MAPPED: {
    en: 'A tax account is not mapped in the chart of accounts. Ask your accountant to map it, then try again.',
    'pt-BR':
      'Uma conta de imposto não está mapeada no plano de contas. Peça ao contador para mapeá-la e tente novamente.',
  },
  // A write named an INACTIVE department or cost center (`dimensionTargetInactive` in
  // apps/api `ap/common/dimension-errors.ts`). One sentence for every screen that saves one.
  DIMENSION_TARGET_INACTIVE: {
    en: 'The selected department or cost center is inactive. Choose an active one and try again.',
    'pt-BR':
      'O departamento ou centro de custo selecionado está inativo. Escolha um ativo e tente novamente.',
  },
  AP_BILL_VENDOR_RULE_DIVERGENCE_OPEN: {
    en: "The document's values differ from the vendor rule. Choose how to resolve the divergence in the withholdings section before sending for approval.",
    'pt-BR': ERROR_CODE_MESSAGES.AP_BILL_VENDOR_RULE_DIVERGENCE_OPEN,
  },
  // ALC-4621 (D43): an automation's change was rolled back because its activity-log row failed.
  WORKFLOW_RUN_LOG_WRITE_FAILED: WORKFLOW_RUN_LOG_WRITE_FAILED_COPY,
  // ALC-4499 (ADR 0027 D12): the API refused a bill save because the bill-change capture failed.
  // Same sentences as packages/db `BILL_CHANGE_CAPTURE_FAILED_COPY` (the web cannot import it).
  BILL_CHANGE_CAPTURE_FAILED: {
    en: 'The change to this bill could not be recorded, so it was not saved. Please try again.',
    'pt-BR':
      'Não foi possível registrar a alteração desta conta a pagar, por isso ela não foi salva. Tente novamente.',
  },
  // ALC-4504 (ADR 0027, D36, D37): the worker relay's stable codes (@erp/services
  // `bill-change-relay-errors.ts`). This is the ONLY copy of these sentences: the services
  // package keeps the codes, not the text, so the two cannot drift.
  BILL_CHANGE_PUBLISH_FAILED: {
    en: 'Changes to this bill could not be sent for rule checking. They are queued and will be retried automatically.',
    'pt-BR':
      'Não foi possível enviar as alterações desta conta a pagar para a verificação de regras. Elas estão na fila e serão reenviadas automaticamente.',
  },
  BILL_CHANGE_PUBLISHER_NOT_CONFIGURED: {
    en: 'Rule checking is not configured in this environment, so bill changes are not being sent.',
    'pt-BR':
      'A verificação de regras não está configurada neste ambiente, por isso as alterações de contas a pagar não estão sendo enviadas.',
  },
  BILL_CHANGE_RELAY_STUCK: {
    en: 'Changes to this bill have failed to send for rule checking for more than 15 minutes. They are kept and still being retried.',
    'pt-BR':
      'As alterações desta conta a pagar não conseguem ser enviadas para a verificação de regras há mais de 15 minutos. Elas continuam guardadas e sendo reenviadas.',
  },
  BILL_CHANGE_RELAY_BACKLOG: {
    en: 'Bill changes are waiting longer than usual to be sent for rule checking. They are queued and will be sent.',
    'pt-BR':
      'As alterações de contas a pagar estão aguardando mais que o normal para serem enviadas à verificação de regras. Elas estão na fila e serão enviadas.',
  },
  BILL_CHANGE_RELAY_RLS_BLOCKED: {
    en: 'Bill changes cannot be sent for rule checking because the database access for this job is incomplete. They are kept until it is fixed.',
    'pt-BR':
      'As alterações de contas a pagar não podem ser enviadas para a verificação de regras porque o acesso ao banco de dados desta rotina está incompleto. Elas ficam guardadas até a correção.',
  },
  // ALC-4624 (ADR 0029, B1): the fail-open capture on payments, alerts and reconciliation lost a
  // bill mark and the capture sweep re-marked it. Sentry-only today; the only copy of this sentence.
  BILL_CHANGE_CAPTURE_DEGRADED: {
    en: 'A payment, alert or reconciliation change was saved, but the bill was not queued for rule checking at once. It was queued again a moment later.',
    'pt-BR':
      'Uma alteração de pagamento, alerta ou conciliação foi salva, mas a conta a pagar não entrou na fila de verificação de regras na hora. Ela foi colocada na fila logo depois.',
  },
  // ALC-4505 (ADR 0027, D13, D36): the worker's bill-events consumer's stable codes (@erp/services
  // `bill-events-consumer-errors.ts`). Sentry-only today (no endpoint returns them); this is the ONLY
  // copy of these sentences, ready for any screen that surfaces them.
  BILL_EVENTS_MESSAGE_DEAD_LETTERED: {
    en: 'A change to this bill could not be checked against the rules after repeated attempts. It was set aside for review.',
    'pt-BR':
      'Não foi possível verificar uma alteração desta conta a pagar contra as regras após várias tentativas. Ela foi separada para análise.',
  },
  BILL_EVENTS_MESSAGE_MALFORMED: {
    en: 'A bill-change message was unreadable, so it was set aside for review instead of being checked.',
    'pt-BR':
      'Uma mensagem de alteração de conta a pagar estava ilegível, por isso foi separada para análise em vez de ser verificada.',
  },
  BILL_EVENTS_SESSION_LOCK_LOST: {
    en: 'The rule check for a bill was interrupted because its place in the queue was lost. It will be retried automatically.',
    'pt-BR':
      'A verificação de regras de uma conta a pagar foi interrompida porque ela perdeu o lugar na fila. Será repetida automaticamente.',
  },
  BILL_EVENTS_RECEIVER_FAILED: {
    en: 'Bill changes could not be received for rule checking. They stay queued and will be checked once receiving recovers.',
    'pt-BR':
      'Não foi possível receber as alterações de contas a pagar para a verificação de regras. Elas continuam na fila e serão verificadas quando o recebimento voltar ao normal.',
  },
  BILL_EVENTS_HANDLER_TIMED_OUT: {
    en: 'The rule check for a change to this bill took too long and was stopped. It will be retried automatically.',
    'pt-BR':
      'A verificação de regras de uma alteração desta conta a pagar demorou demais e foi interrompida. Será repetida automaticamente.',
  },
  BILL_EVENTS_CONSUMER_NOT_CONFIGURED: {
    en: 'Receiving bill changes for rule checking is not configured in this environment, so they are not being checked.',
    'pt-BR':
      'O recebimento das alterações de contas a pagar para a verificação de regras não está configurado neste ambiente, por isso elas não estão sendo verificadas.',
  },
  // ALC-4517 (D40): the consumer's two load signals, raised from its per-minute stats (Sentry-only,
  // at most one event per code per replica per hour). Codes live in @erp/services
  // `bill-events-consumer-errors.ts`.
  BILL_EVENTS_CONSUMER_LAGGING: {
    en: 'The rule check for bill changes is running behind the queue. Changes stay queued and will be checked.',
    'pt-BR':
      'A verificação de regras das alterações de contas a pagar está atrasada em relação à fila. As alterações continuam na fila e serão verificadas.',
  },
  BILL_EVENTS_CONSUMER_SATURATED: {
    en: 'The rule checks for bill changes are running at full capacity. Changes stay queued and will be checked, but later than usual.',
    'pt-BR':
      'As verificações de regras das alterações de contas a pagar estão usando toda a capacidade. As alterações continuam na fila e serão verificadas, mas mais tarde que o normal.',
  },
  // ALC-4494 (D17): a worker sweep recovers a bill stuck in intake for too long. Today this is
  // Sentry-only (no endpoint returns it); the copy is here so it is ready if a future screen
  // surfaces it. The code constant lives in packages/services `bill-intake-state.util.ts`.
  BILL_INTAKE_STALE: {
    en: 'A bill was stuck being created or updated for too long, so it was marked as finished.',
    'pt-BR': 'Uma conta a pagar ficou tempo demais em processamento e foi marcada como concluída.',
  },
  // ALC-4502 (ADR 0027, phase 1): the bill-updated checker failed to evaluate a bill change.
  // The only copy of this code's text: the stable code itself lives in packages/services.
  BILL_UPDATED_CHECK_FAILED: {
    en: 'The bill-updated rules could not be checked for this change. It will be retried.',
    'pt-BR':
      'Não foi possível verificar as regras de alteração de conta para esta mudança. Será repetido.',
  },
  BILL_UPDATED_CONDITION_INVALID: {
    en: 'A bill-updated rule has a condition that cannot be evaluated (unknown field or operator), so it never matches. Fix or remove that condition.',
    'pt-BR':
      'Uma regra de alteração de conta tem uma condição que não pode ser avaliada (campo ou operador desconhecido), então ela nunca é atendida. Corrija ou remova essa condição.',
  },
  WORKFLOW_ENTITY_MISMATCH: {
    en: 'The rule references classifications from different entities. Use classifications from a single entity.',
    'pt-BR': ERROR_CODE_MESSAGES.WORKFLOW_ENTITY_MISMATCH,
  },
  WORKFLOW_ENTITY_REQUIRED: {
    en: 'Alchemy could not determine the entity for this rule. Select valid classifications from a single entity.',
    'pt-BR': ERROR_CODE_MESSAGES.WORKFLOW_ENTITY_REQUIRED,
  },
  WORKFLOW_ENTITY_NOT_FOUND: {
    en: 'The selected entity does not exist or is not available for this tenant.',
    'pt-BR': ERROR_CODE_MESSAGES.WORKFLOW_ENTITY_NOT_FOUND,
  },
  WORKFLOW_ENTITY_NOT_APPLICABLE: {
    en: 'This workflow trigger does not support an entity binding on the rule.',
    'pt-BR': ERROR_CODE_MESSAGES.WORKFLOW_ENTITY_NOT_APPLICABLE,
  },
  WORKFLOW_ENTITY_UNRESOLVED_REFERENCE: {
    en: 'A classification referenced by the rule is missing or has no valid entity binding.',
    'pt-BR': ERROR_CODE_MESSAGES.WORKFLOW_ENTITY_UNRESOLVED_REFERENCE,
  },
  [WorkflowConditionFactErrorCode.FIELD_NOT_ALLOWED_FOR_TRIGGER]: {
    en: 'This field can only be used in bill classification rules.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionFactErrorCode.FIELD_NOT_ALLOWED_FOR_TRIGGER],
  },
  [WorkflowConditionFactErrorCode.OPERATOR_NOT_ALLOWED]: {
    en: 'The selected operator cannot be used with this field.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionFactErrorCode.OPERATOR_NOT_ALLOWED],
  },
  [WorkflowConditionFactErrorCode.VALUE_INVALID]: {
    en: 'The value is not valid for this field.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionFactErrorCode.VALUE_INVALID],
  },
  [WorkflowConditionFactErrorCode.PROFILE_RATE_REQUIRED]: {
    en: 'Enter the rate of every tax in the withholding profile.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionFactErrorCode.PROFILE_RATE_REQUIRED],
  },
  [WorkflowConditionFactErrorCode.SPLIT_SUM_NOT_100]: {
    en: 'The percentages of each split dimension must add up to exactly 100.00%.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionFactErrorCode.SPLIT_SUM_NOT_100],
  },
  [WorkflowConditionErrorCode.OPERATOR_INVALID]: {
    en: 'This condition operator does not exist.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionErrorCode.OPERATOR_INVALID],
  },
  [WorkflowConditionErrorCode.FIELD_UNSUPPORTED]: {
    en: 'This field cannot be used in conditions for this trigger.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionErrorCode.FIELD_UNSUPPORTED],
  },
  [WorkflowConditionErrorCode.OPERATOR_UNSUPPORTED_FOR_FIELD]: {
    en: 'This operator cannot be used with this field.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionErrorCode.OPERATOR_UNSUPPORTED_FOR_FIELD],
  },
  [WorkflowConditionErrorCode.LINE_QUANTIFIER_NOT_LINE_FIELD]: {
    en: 'Choosing "at least one line" or "every line" only works on conditions about the bill line items.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionErrorCode.LINE_QUANTIFIER_NOT_LINE_FIELD],
  },
  [WorkflowConditionErrorCode.LINE_QUANTIFIER_TRIGGER_UNSUPPORTED]: {
    en: 'Conditions about the bill line items only apply to bill classification rules.',
    'pt-BR': ERROR_CODE_MESSAGES[WorkflowConditionErrorCode.LINE_QUANTIFIER_TRIGGER_UNSUPPORTED],
  },
  [WorkflowTriggerErrorCode.NOT_RUNNABLE]: WORKFLOW_TRIGGER_NOT_RUNNABLE_COPY,
  WORKFLOW_BANK_ACCOUNT_ENTITY_MISMATCH: {
    en: 'The bank account must be active and belong to the workflow entity.',
    'pt-BR': 'A conta bancária deve estar ativa e pertencer à entidade do fluxo.',
  },
  BILL_PREFERRED_BANK_ENTITY_MISMATCH: {
    en: 'The preferred bank account must be active and belong to the bill entity.',
    'pt-BR': 'A conta bancária preferencial deve estar ativa e pertencer à entidade da conta.',
  },
  BILL_PREFERRED_BANK_CARD_METHOD: {
    en: 'A preferred bank account cannot be set for a card payment method.',
    'pt-BR': 'Não é possível definir uma conta bancária preferencial para pagamento com cartão.',
  },
  BILL_PREFERRED_BANK_STATUS_NOT_EDITABLE: {
    en: 'The preferred bank account cannot be changed on a paid or cancelled bill.',
    'pt-BR': 'A conta bancária preferencial não pode ser alterada em uma conta paga ou cancelada.',
  },
  WORKFLOW_BANK_ACCOUNT_UNAVAILABLE_AT_APPLY: {
    en: 'The workflow bank account is no longer available for this bill.',
    'pt-BR': 'A conta bancária do fluxo não está mais disponível para esta conta.',
  },
  PREFERRED_BANK_UNAVAILABLE: {
    en: 'The preferred bank account was unavailable; the eligible entity default was used.',
    'pt-BR':
      'A conta bancária preferencial estava indisponível; foi usado o padrão elegível da entidade.',
  },
  AP_BILL_NET_AMOUNT_MISMATCH: {
    en: 'The bill net amount does not match the gross amount, additions and withholdings. Fix the amounts before sending for approval.',
    'pt-BR':
      'O líquido da conta não corresponde ao bruto com acréscimos e retenções. Corrija os valores antes de enviar para aprovação.',
  },
  REIMBURSEMENT_POLICY_ENTITY_REQUIRED: {
    en: 'Select the policy entity before using entity-bound classification criteria.',
    'pt-BR': 'Selecione a entidade da política antes de usar critérios de classificação.',
  },
  REIMBURSEMENT_POLICY_WORKFLOW_ENTITY_MISMATCH: {
    en: 'The selected workflow is bound to a different entity than the reimbursement policy.',
    'pt-BR':
      'O fluxo selecionado está vinculado a uma entidade diferente da política de reembolso.',
  },
  // ALC-4673: creating a customer is refused when the same party already has an ACTIVE customer
  // in the entity (the MCP tool and the External API return the same code). The existing
  // customer's id and code ride in the error details.
  CUSTOMER_ALREADY_EXISTS: {
    en: 'An active customer already exists for this person or company in this entity. Use the existing customer instead of creating another.',
    'pt-BR':
      'Já existe um cliente ativo para esta pessoa ou empresa nesta entidade. Use o cliente existente em vez de criar outro.',
  },
  // A pre-billing row names a billing service that has no tax set-up (a bare service synced from
  // the CRM has no ISS rate), so its invoice taxes cannot be calculated.
  FISCAL_PARAMETERS_MISSING: {
    en: 'Tax set-up is missing, such as the ISS rate of the billing service. Complete it, then retry the failed rows.',
    'pt-BR':
      'Falta configuração fiscal, como a alíquota de ISS do serviço de cobrança. Complete e tente de novo.',
  },
  // An AR import row with no code and no CPF/CNPJ names its buyer by e-mail, and two customers of
  // the entity have that e-mail.
  AR_CUSTOMER_EMAIL_AMBIGUOUS: {
    en: 'More than one customer of this entity has this e-mail. Fill in the customer code or the CPF/CNPJ on the row.',
    'pt-BR':
      'Mais de um cliente desta entidade tem este e-mail. Preencha o código do cliente ou o CPF/CNPJ na linha.',
  },
  // ALC-4673 (D10): changing a customer's document is refused until someone says what the change
  // means. The customer screen asks before it saves, so this is the answer for any other caller
  // that reaches the user (an import line, an integration).
  CUSTOMER_TAX_ID_CHANGE_CHOICE_REQUIRED: {
    en: 'Say whether the new document corrects the same company or person, or belongs to a different one.',
    'pt-BR':
      'Informe se o novo documento corrige a mesma empresa ou pessoa, ou se é de outra empresa ou pessoa.',
  },
  // ALC-4673 (D10): the document typed already belongs to another record that cannot take this
  // customer (it is a vendor, an employee, or a customer elsewhere).
  CUSTOMER_TAX_ID_IN_USE: {
    en: 'This document already belongs to another record in the system. Check the number, or use that record instead.',
    'pt-BR':
      'Este documento já pertence a outro cadastro do sistema. Confira o número ou use esse cadastro.',
  },
  // ALC-4673 (D11): the corrected document is of a type that belongs to another country than the
  // one on the customer, and a vendor record shares the customer. The country is the customer's own,
  // so the save is refused until it is set with the correction.
  CUSTOMER_TAX_ID_COUNTRY_MISMATCH: {
    en: 'The country of this customer does not match the new document type. Set the country the document belongs to (United States for an EIN, Mexico for an RFC, Brazil for a CNPJ or CPF, any other country for an international id) and save again.',
    'pt-BR':
      'O país deste cliente não combina com o tipo do novo documento. Informe o país a que o documento pertence (Estados Unidos para EIN, México para RFC, Brasil para CNPJ ou CPF, outro país para documento internacional) e salve de novo.',
  },
  // ALC-4673 (R8-SF5): a change would replace a customer's document with a number that is not valid
  // for its type: a CPF or CNPJ whose check digits do not add up, or, for a customer that is also
  // a vendor, an RFC or an EIN in a format the vendor rule refuses. The change is refused and the
  // customer keeps its document.
  CUSTOMER_TAX_ID_INVALID: {
    en: 'The document number is not valid for its type. Check the digits and the format, and try again. The customer keeps the document it has.',
    'pt-BR':
      'O número do documento não é válido para o tipo informado. Confira os dígitos e o formato e tente de novo. O cliente continua com o documento que já tem.',
  },
  // ALC-4673 (R8-SF5): an import row would change a customer's document but names no type and
  // the number is neither a CPF nor a CNPJ, so the type would be a guess.
  CUSTOMER_TAX_ID_TYPE_UNKNOWN: {
    en: 'Say which kind of document this is (CPF, CNPJ or another type). The customer keeps the document it has.',
    'pt-BR':
      'Informe de que tipo é o documento (CPF, CNPJ ou outro). O cliente continua com o documento que já tem.',
  },
};

/**
 * Maps a validation error to a user-friendly message.
 *
 * If the error has a recognized code, returns the mapped message in the viewer's language when the
 * code has a localized copy (pt-BR otherwise). Otherwise, returns the original error message.
 *
 * @param error The validation error from the backend
 * @returns User-friendly message (pt-BR unless the code has an English copy)
 */
export function mapValidationErrorToMessage(error: ValidationError): string {
  if (error.code === 'REQUIRED_FIELD_MISSING') {
    if (error.field === 'documents') {
      return i18n.t('billDetails.validation.attachmentRequired', { ns: 'bills' });
    }

    const message = REQUIRED_FIELD_MESSAGES[error.field];
    if (message) {
      return message;
    }
    return ERROR_CODE_MESSAGES.REQUIRED_FIELD_MISSING;
  }

  if (error.code === 'REQUIRED_CUSTOM_FIELD_MISSING') {
    // Backend messages are informative (e.g. "Custom field <name> is required"
    // or "Custom field <name> is required for line item <id>"). Surface them
    // translated so the user knows which custom field is missing.
    const translated = translateCustomFieldMessage(error.message);
    return translated ?? ERROR_CODE_MESSAGES.REQUIRED_CUSTOM_FIELD_MISSING;
  }

  const locale = i18n.language?.startsWith('en') ? 'en' : 'pt-BR';
  const mappedMessage = getValidationErrorMessageByCode(error.code, locale, error.details);
  if (mappedMessage) {
    return mappedMessage;
  }
  // Fall back to the original message if no mapping exists
  return error.message;
}

/** Returns localized copy for a stable backend code when one is registered. */
export function getValidationErrorMessageByCode(
  code: string | undefined,
  locale: 'en' | 'pt-BR' = 'pt-BR',
  details?: Readonly<Record<string, unknown>> | null,
): string | undefined {
  if (!code) return undefined;
  const localized = Object.hasOwn(LOCALIZED_ERROR_CODE_MESSAGES, code)
    ? LOCALIZED_ERROR_CODE_MESSAGES[code]?.[locale]
    : undefined;
  const mapped = Object.hasOwn(ERROR_CODE_MESSAGES, code) ? ERROR_CODE_MESSAGES[code] : undefined;
  return (
    localized ??
    mapped ??
    revenueTaxEngineMessage(code, locale) ??
    revenueCycleMessage(code, locale) ??
    workflowOriginMessage(code, locale) ??
    workflowDescriptionMessage(code, locale) ??
    costServiceMessage(code, locale, details ?? {})
  );
}

/**
 * The origin of a rule (ALC-4564) answers with a stable code. The shared copy in `@alinea/types`
 * holds both languages, so the web does not keep a second copy of those sentences.
 */
function workflowOriginMessage(code: string, locale: 'en' | 'pt-BR'): string | undefined {
  if (!Object.hasOwn(WORKFLOW_ORIGIN_ERROR_MESSAGES, code)) return undefined;
  const copy = WORKFLOW_ORIGIN_ERROR_MESSAGES[code as WorkflowOriginErrorCode];
  return locale === 'en' ? copy.en : copy.ptBr;
}

/**
 * Describing a rule in words (ALC-4564) refuses with a stable code, each with both languages in the
 * shared copy. The compiler failing (502) and the 200 refusals use the same table.
 */
function workflowDescriptionMessage(code: string, locale: 'en' | 'pt-BR'): string | undefined {
  if (!Object.hasOwn(WORKFLOW_DESCRIPTION_ERROR_MESSAGES, code)) return undefined;
  const copy = WORKFLOW_DESCRIPTION_ERROR_MESSAGES[code as WorkflowDescriptionErrorCode];
  return locale === 'en' ? copy.en : copy.ptBr;
}

/**
 * The Revenue Tax engine answers with a stable code (ADR 0024). The shared copy in `@alinea/types`
 * holds both languages, so the web does not keep a third copy of those sentences.
 */
function revenueTaxEngineMessage(code: string, locale: 'en' | 'pt-BR'): string | undefined {
  if (!Object.hasOwn(REVENUE_TAX_ERROR_MESSAGES, code)) return undefined;
  const copy = REVENUE_TAX_ERROR_MESSAGES[code as keyof typeof REVENUE_TAX_ERROR_MESSAGES];
  return locale === 'en' ? copy.en : copy.ptBR;
}

/**
 * Revenue cycles (ciclo de receita, ALC-5089) refuse with a stable code; the shared copy in
 * `@alinea/types` holds both languages, the same sentences the API sends.
 */
function revenueCycleMessage(code: string, locale: 'en' | 'pt-BR'): string | undefined {
  if (!Object.hasOwn(REVENUE_CYCLE_ERROR_MESSAGES, code)) return undefined;
  const copy = REVENUE_CYCLE_ERROR_MESSAGES[code as keyof typeof REVENUE_CYCLE_ERROR_MESSAGES];
  return locale === 'en' ? copy.en : copy.ptBR;
}

/**
 * Cost Services answers with a stable code too (ALC-4474). Its shared copy holds both languages
 * as well, so the web renders it from there instead of keeping another copy. D45c: the copy is
 * filled with the error's details like the API fills it (optional `[[ … ]]` segments drop when a
 * value is missing); when a required `{value}` is still missing the caller falls back to the
 * API's own, already filled, sentence instead of showing a raw placeholder.
 */
function costServiceMessage(
  code: string,
  locale: 'en' | 'pt-BR',
  details: Readonly<Record<string, unknown>>,
): string | undefined {
  if (!Object.hasOwn(COST_SERVICE_ERROR_MESSAGES, code)) return undefined;
  const copy = COST_SERVICE_ERROR_MESSAGES[code as keyof typeof COST_SERVICE_ERROR_MESSAGES];
  const filled =
    locale === 'en'
      ? fillCostServiceCopy(copy.en, details, 'en')
      : fillCostServiceCopy(copy.ptBR, details, 'ptBR');
  return /\{\w+\}/.test(filled) ? undefined : filled;
}

/**
 * Backend builds custom-field messages like:
 *   - "Custom field <Name> is required"
 *   - "Custom field <Name> is required for line item <lineItemId>"
 * Translate the known patterns to pt-BR while preserving the custom-field name.
 */
function translateCustomFieldMessage(message: string): string | undefined {
  const lineItemMatch = message.match(/^Custom field (.+) is required for line item (.+)$/);
  if (lineItemMatch) {
    const [, fieldName, lineItemId] = lineItemMatch;
    return `Campo customizado "${fieldName}" é obrigatório no item ${lineItemId}`;
  }

  const billLevelMatch = message.match(/^Custom field (.+) is required$/);
  if (billLevelMatch) {
    const [, fieldName] = billLevelMatch;
    return `Campo customizado "${fieldName}" é obrigatório`;
  }

  return undefined;
}

/**
 * Maps an array of validation errors to user-friendly Portuguese messages.
 *
 * @param errors Array of validation errors from the backend
 * @returns Array of user-friendly messages in Portuguese
 */
export function mapValidationErrorsToMessages(errors: ValidationError[]): string[] {
  return errors.map(mapValidationErrorToMessage);
}

/**
 * Maps raw English API error messages (no structured code) to friendly pt-BR strings.
 * Used as a last-resort fallback for BadRequestExceptions that do not carry a code,
 * such as the GL posting guard errors thrown directly by the bill service.
 *
 * Returns undefined when no mapping is found, so callers can fall back gracefully.
 */
export function mapRawApiMessageToPtBr(message: string | undefined): string | undefined {
  if (!message) return undefined;

  const RAW_MESSAGE_MAP: [RegExp | string, string][] = [
    [
      'Total amount cannot be changed after the bill is posted to the GL',
      'O valor total não pode ser alterado após o lançamento contábil da conta',
    ],
    [
      'Competency date cannot be changed after the bill is posted to the GL',
      'A data de competência não pode ser alterada após o lançamento contábil da conta',
    ],
    [
      'Entity cannot be changed after the bill is posted to the GL',
      ERROR_CODE_MESSAGES.BILL_ENTITY_CHANGE_BLOCKED_BY_GL_POSTING,
    ],
    [
      'Status changes must use POST /ap/bills/:billId/transition-status',
      'Alterações de status devem ser feitas pelo fluxo correto',
    ],
    [
      /^Transition from .+ to .+ is not allowed$/,
      'Essa transição de status não é permitida para esta conta',
    ],
    [/^Invalid bill status/, 'Status da conta inválido'],
    [/^Invalid target status/, 'Status de destino inválido'],
    [
      'Reimbursement request is required for approval',
      'As informações do reembolso são obrigatórias para aprovação',
    ],
    [/^Bill \S+ has no totalAmount$/, 'A conta não possui valor total definido'],
    [/^Bill \S+ not found$/, 'Conta não encontrada'],
    [
      'Only DRAFT or SUBMITTED bills can be deleted',
      'Apenas contas em rascunho ou submetidas podem ser excluídas',
    ],
  ];

  for (const [pattern, ptBr] of RAW_MESSAGE_MAP) {
    if (typeof pattern === 'string' ? message === pattern : pattern.test(message)) {
      return ptBr;
    }
  }

  return undefined;
}
